/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

// The shadcn primitive doesn't re-export `createHandle`, so import Base UI's dialog directly.
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import type { FormStore } from "@formisch/react"
import { Form, Field as FormField, getInput, useForm } from "@formisch/react"
import { CirclePlusIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import type { ComponentProps } from "react"
import { useRef, useState } from "react"
import * as v from "valibot"

import {
    applicationTypeCategories,
    CreateApplicationTypeSchema
} from "../../features/application-type/application-type-schema"
import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { isFieldInvalid } from "../../utils/is-field-invalid"
import { useViewModel } from "../app/app-hooks"
import { BusyButton } from "../components/busy-button"
import { IconPicker } from "../components/icon-picker"
import { Button } from "../primitives/button"
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "../primitives/dialog"
import { Field, FieldError, FieldGroup, FieldLabel } from "../primitives/field"
import { Input } from "../primitives/input"
import { NativeSelect, NativeSelectOption } from "../primitives/native-select"
import styles from "./create-application-type.module.css"
import { PlatformDropdown } from "./platform-dropdown"

/**
 * Connects the dialog to triggers rendered outside `CreateApplicationType`.
 *
 * The empty list's link uses it through `CreateApplicationTypeTrigger`.
 */
const dialogHandle = DialogPrimitive.createHandle()

/**
 * Renders the Create Application button and its dialog.
 *
 * The open state lives here, so the form can close the dialog once the
 * application exists.
 *
 * Focus returns to this button, whichever trigger opened the dialog. The empty
 * list's link disappears when the first application is created, so it can't
 * take focus back.
 */
export const CreateApplicationType = () => {
    const [open, setOpen] = useState(false)
    const triggerRef = useRef<HTMLButtonElement>(null)

    return (
        <Dialog handle={dialogHandle} open={open} onOpenChange={setOpen}>
            <DialogTrigger ref={triggerRef} render={<Button className="font-semibold uppercase" />}>
                <CirclePlusIcon data-icon="inline-start" />
                Create Application
            </DialogTrigger>
            <DialogContent finalFocus={triggerRef}>
                <DialogHeader>
                    <DialogTitle className="m-0 text-xl leading-6 font-semibold uppercase">
                        Create Application
                    </DialogTitle>
                </DialogHeader>
                <CreateApplicationTypeForm onCreated={() => setOpen(false)} />
            </DialogContent>
        </Dialog>
    )
}

/**
 * Opens the Create Application dialog from elsewhere on the page, such as the
 * empty list's link.
 *
 * Takes the props of `DialogTrigger` without `handle`, because the shared
 * handle is what connects it to the dialog. `CreateApplicationType` renders
 * the dialog, so it must be on the same page.
 */
export const CreateApplicationTypeTrigger = (props: Omit<ComponentProps<typeof DialogTrigger>, "handle">) => (
    <DialogTrigger handle={dialogHandle} {...props} />
)

interface CreateApplicationTypeFormProps {
    /**
     * Called once the form has created the application, uploaded any chosen
     * icon and reloaded the list.
     */
    onCreated: () => void
}

/**
 * Form for a new application and its optional icon.
 *
 * The dialog unmounts the form on close, so every opening starts from the
 * defaults. The organization defaults to the one selected in the console, the
 * platform to Android and the category to DEV.
 *
 * Create stays disabled until the input passes `CreateApplicationTypeSchema`,
 * and while the requests run.
 */
const CreateApplicationTypeForm = observer(({ onCreated }: CreateApplicationTypeFormProps) => {
    const appTypeVM = useViewModel(ApplicationTypeViewModel)
    const orgVM = useViewModel(OrganizationViewModel)

    const form = useForm({
        schema: CreateApplicationTypeSchema,
        initialInput: {
            organizationId: orgVM.selectedOrgId === "none" ? "" : String(orgVM.selectedOrgId),
            platform: "ANDROID",
            category: "DEV"
        },
        validate: "blur"
    })

    /**
     * Creates the application, uploads its icon if one was chosen, reloads the
     * list and closes the dialog. Create shows as busy until the last request
     * finishes.
     *
     * A failed request stops there. The service shows the error as a toast, and
     * the dialog stays open with the entered values.
     */
    const create = async (input: v.InferOutput<typeof CreateApplicationTypeSchema>) => {
        const result = await appTypeVM.createAppType(
            input.organizationId,
            input.description,
            input.sourcePackageName,
            input.platform,
            input.category
        )
        if (!result.success) return

        if (input.icon) {
            // The API type marks `id` optional, but it's a primitive `long` on the Java
            // record, so a created application always has one.
            const upload = await appTypeVM.updateAppTypeIcon(result.data?.id as number, input.icon)
            if (!upload.success) return
        }

        await appTypeVM.getAllAppTypes()
        onCreated()
    }

    return (
        <Form of={form} onSubmit={create}>
            <DialogBody>
                <FieldGroup>
                    <FormField of={form} path={["organizationId"]}>
                        {(field) => {
                            const invalid = isFieldInvalid(field)

                            return (
                                <Field data-invalid={invalid}>
                                    <FieldLabel htmlFor="application-type-organizationId">Organization</FieldLabel>
                                    <NativeSelect
                                        {...field.props}
                                        id="application-type-organizationId"
                                        value={field.input}
                                        aria-invalid={invalid}
                                        // With one organization there's nothing to choose, so lock the
                                        // select. `loadOrganizations` preselects that organization, so
                                        // the locked select never sits on the placeholder.
                                        disabled={orgVM.organizations.length === 1}
                                        className={styles.control}
                                    >
                                        <NativeSelectOption value="" disabled>
                                            Select organization...
                                        </NativeSelectOption>
                                        {orgVM.organizations.map((org) => (
                                            <NativeSelectOption key={org.id} value={org.id}>
                                                {org.name}
                                            </NativeSelectOption>
                                        ))}
                                    </NativeSelect>
                                    {invalid && <FieldError>{field.errors?.[0]}</FieldError>}
                                </Field>
                            )
                        }}
                    </FormField>

                    <TextField form={form} path="sourcePackageName" label="Package Name" />
                    <TextField form={form} path="description" label="Description" />

                    <FormField of={form} path={["icon"]}>
                        {(field) => (
                            <Field>
                                <FieldLabel htmlFor="application-type-icon">Icon</FieldLabel>
                                <IconPicker
                                    id="application-type-icon"
                                    value={field.input}
                                    onChange={field.onChange}
                                    disabled={form.isSubmitting}
                                />
                            </Field>
                        )}
                    </FormField>

                    <FormField of={form} path={["platform"]}>
                        {(field) => (
                            <Field>
                                <FieldLabel id="application-type-platform-label" htmlFor="application-type-platform">
                                    Platform
                                </FieldLabel>
                                <PlatformDropdown
                                    id="application-type-platform"
                                    labelId="application-type-platform-label"
                                    value={field.input}
                                    onChange={field.onChange}
                                />
                            </Field>
                        )}
                    </FormField>

                    <FormField of={form} path={["category"]}>
                        {(field) => (
                            <Field>
                                <FieldLabel htmlFor="application-type-category">Category</FieldLabel>
                                <NativeSelect
                                    {...field.props}
                                    id="application-type-category"
                                    value={field.input}
                                    className={styles.control}
                                >
                                    {applicationTypeCategories.map((category) => (
                                        <NativeSelectOption key={category} value={category}>
                                            {category}
                                        </NativeSelectOption>
                                    ))}
                                </NativeSelect>
                            </Field>
                        )}
                    </FormField>
                </FieldGroup>
            </DialogBody>

            <DialogFooter>
                <DialogClose render={<Button variant="secondary" size="action" />}>Cancel</DialogClose>
                <BusyButton
                    type="submit"
                    size="action"
                    busy={form.isSubmitting}
                    disabled={!v.is(CreateApplicationTypeSchema, getInput(form))}
                >
                    Create
                </BusyButton>
            </DialogFooter>
        </Form>
    )
})

interface TextFieldProps {
    /**
     * The dialog's form store.
     */
    form: FormStore<typeof CreateApplicationTypeSchema>
    /**
     * Schema field the input edits. The input's id is `application-type-<path>`.
     */
    path: "sourcePackageName" | "description"
    /**
     * Visible label, which is also the input's accessible name.
     */
    label: string
}

/**
 * Renders a text input with its label.
 *
 * The validation error appears once the user has edited the field and left it.
 */
const TextField = ({ form, path, label }: TextFieldProps) => {
    const id = `application-type-${path}`

    return (
        <FormField of={form} path={[path]}>
            {(field) => {
                const invalid = isFieldInvalid(field)

                return (
                    <Field data-invalid={invalid}>
                        <FieldLabel htmlFor={id}>{label}</FieldLabel>
                        <Input
                            {...field.props}
                            id={id}
                            type="text"
                            value={field.input}
                            aria-invalid={invalid}
                            autoComplete="off"
                            className="h-10"
                        />
                        {invalid && <FieldError>{field.errors?.[0]}</FieldError>}
                    </Field>
                )
            }}
        </FormField>
    )
}
